import type { ReactNode, Ref } from 'react';
import { forwardRef, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { ChatHintType } from '@trinity/chat';
import type { Chat } from '@trinity/chat';
import * as Presentation from './presentation';
import { useFloating, useHover, useInteractions } from '@floating-ui/react';

type Props = {
  className?: string;
  hint: Chat.QueryHint.Item;
  onClick: () => unknown;
  disabled?: boolean;
};

export const MessageQueryHint = (props: Props) => {
  switch (props.hint.type) {
    case ChatHintType.FindQuotes:
      return <AnalysisQueryHint {...props} value={props.hint.displayValue} />;
    case ChatHintType.AnalysisTags:
      return <AnalysisQueryHint {...props} value={props.hint.displayValue} />;
    case ChatHintType.Text:
      return <TextQueryHint {...props} value={props.hint.displayValue} />;
    case ChatHintType.Chart:
      return <ChartQueryHint {...props} value={props.hint.displayValue} />;
    case ChatHintType.Prompt:
      return <AnalysisQueryHint {...props} value={props.hint.displayValue} />;
    default:
      throw new UnreachableCaseError(props.hint.type);
  }
};

type ChartQueryHintProps = {
  className?: string;
  value: ReactNode;
  onClick: () => unknown;
  disabled?: boolean;
};

export const ChartQueryHint = ({ className, value, disabled, onClick }: ChartQueryHintProps) => {
  return (
    <Presentation.Root className={className} onClick={disabled ? undefined : onClick}>
      <TextHintWrap isDisabled={disabled}>
        <TimelineIcon isDisabled={disabled} /> {value}
      </TextHintWrap>
    </Presentation.Root>
  );
};

export const QuantQueryHint = ({
  className,
  hint,
  disabled,
  onClick,
  tooltip,
}: Props & { tooltip?: React.ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'right',
  });
  const hover = useHover(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

  return (
    <Presentation.Root
      className={className}
      onClick={disabled ? undefined : onClick}
      ref={refs.setReference}
      {...getReferenceProps()}
    >
      <TextHintWrap isDisabled={disabled}>
        <TextHintIcon isDisabled={disabled} /> {hint.displayValue}
      </TextHintWrap>
      {isOpen && tooltip && (
        <div ref={refs.setFloating} style={floatingStyles} {...getFloatingProps()}>
          {tooltip}
        </div>
      )}
    </Presentation.Root>
  );
};

type TextHintProps = {
  className?: string;
  onClick: () => unknown;
  disabled?: boolean;
  value: ReactNode;
};

export const TextQueryHint = ({ className, value, disabled, onClick }: TextHintProps) => {
  return (
    <Presentation.Root className={className} onClick={disabled ? undefined : onClick}>
      <TextHintWrap isDisabled={disabled}>
        <TextHintIcon isDisabled={disabled} /> {value}
      </TextHintWrap>
    </Presentation.Root>
  );
};

type AnalysisProps = {
  className?: string;
  onClick: () => unknown;
  disabled?: boolean;
  value: string;
};

export const AnalysisQueryHint = forwardRef(
  ({ className, value, disabled, onClick }: AnalysisProps, ref: Ref<HTMLButtonElement>) => {
    return (
      <Presentation.Root className={className} onClick={disabled ? undefined : onClick} ref={ref}>
        <AnalysisWrap isDisabled={disabled}>
          <AnalysisIcon isDisabled={disabled} /> {value}
        </AnalysisWrap>
      </Presentation.Root>
    );
  }
);

type IconQueryHintProps = {
  className?: string;
  onClick: () => unknown;
  disabled?: boolean;
  value: string;
};

export const SearchQueryHint = ({ className, value, disabled, onClick }: IconQueryHintProps) => {
  return (
    <Presentation.Root className={className} onClick={disabled ? undefined : onClick}>
      <AnalysisWrap isDisabled={disabled}>
        <SearchIcon isDisabled={disabled} /> {value}
      </AnalysisWrap>
    </Presentation.Root>
  );
};

export const DownloadQueryHint = ({ className, value, disabled, onClick }: IconQueryHintProps) => {
  return (
    <Presentation.Root className={className} onClick={disabled ? undefined : onClick}>
      <AnalysisWrap isDisabled={disabled}>
        <DownloadIcon isDisabled={disabled} /> {value}
      </AnalysisWrap>
    </Presentation.Root>
  );
};

export const LocationQueryHint = ({ className, value, disabled, onClick }: IconQueryHintProps) => {
  return (
    <Presentation.Root className={className} onClick={disabled ? undefined : onClick}>
      <AnalysisWrap isDisabled={disabled}>
        <LocationIcon isDisabled={disabled} /> {value}
      </AnalysisWrap>
    </Presentation.Root>
  );
};

export const TextHintWrap = styled(Presentation.Wrap)(({ isDisabled, theme }) => {
  const { Text } = useMemo(() => Presentation.Colors(theme), [theme]);

  const values = {
    enabled: Text.enabled,
    disabled: Text.disabled,
  };

  return {
    backgroundColor: `${isDisabled ? values.disabled.backgroundColor : values.enabled.backgroundColor}`,
    '&:hover': {
      backgroundColor: `${isDisabled ? values.disabled.hover.backgroundColor : values.enabled.hover.backgroundColor}`,
    },
  };
});

export const TextHintIcon = styled(Presentation.TextStyledIcon)(({ isDisabled, theme }) => {
  const { TextIcon } = useMemo(() => Presentation.Colors(theme), [theme]);

  const values = {
    enabled: TextIcon.enabled,
    disabled: TextIcon.disabled,
  };

  return {
    color: `${isDisabled ? values.disabled.color : values.enabled.color}`,
  };
});

const AnalysisWrap = styled(Presentation.Wrap)(({ isDisabled, theme }) => {
  const { Analysis } = useMemo(() => Presentation.Colors(theme), [theme]);

  const values = {
    enabled: Analysis.enabled,
    disabled: Analysis.disabled,
  };

  return {
    backgroundColor: `${isDisabled ? values.disabled.backgroundColor : values.enabled.backgroundColor}`,
    '&:hover': {
      backgroundColor: `${isDisabled ? values.disabled.hover.backgroundColor : values.enabled.hover.backgroundColor}`,
    },
  };
});

const AnalysisIcon = styled(Presentation.AnalysisStyledIcon)(({ isDisabled, theme }) => {
  const { AnalysisIcon } = useMemo(() => Presentation.Colors(theme), [theme]);

  const values = {
    enabled: AnalysisIcon.enabled,
    disabled: AnalysisIcon.disabled,
  };

  return {
    color: `${isDisabled ? values.disabled.color : values.enabled.color}`,
  };
});

const TimelineIcon = styled(Presentation.TimelineStyledIcon)(({ isDisabled, theme }) => {
  const { TimelineIcon } = useMemo(() => Presentation.Colors(theme), [theme]);

  const values = {
    enabled: TimelineIcon.enabled,
    disabled: TimelineIcon.disabled,
  };

  return {
    color: `${isDisabled ? values.disabled.color : values.enabled.color}`,
  };
});

const SearchIcon = styled(Presentation.SearchStyledIcon)(({ isDisabled, theme }) => {
  const { DefaultIcon } = useMemo(() => Presentation.Colors(theme), [theme]);

  const values = {
    enabled: DefaultIcon.enabled,
    disabled: DefaultIcon.disabled,
  };

  return {
    color: `${isDisabled ? values.disabled.color : values.enabled.color}`,
  };
});

const DownloadIcon = styled(Presentation.DownloadStyledIcon)(({ isDisabled, theme }) => {
  const { DefaultIcon } = useMemo(() => Presentation.Colors(theme), [theme]);

  const values = {
    enabled: DefaultIcon.enabled,
    disabled: DefaultIcon.disabled,
  };

  return {
    color: `${isDisabled ? values.disabled.color : values.enabled.color}`,
  };
});

const LocationIcon = styled(Presentation.LocationStyledIcon)(({ isDisabled, theme }) => {
  const { DefaultIcon } = useMemo(() => Presentation.Colors(theme), [theme]);

  const values = {
    enabled: DefaultIcon.enabled,
    disabled: DefaultIcon.disabled,
  };

  return {
    color: `${isDisabled ? values.disabled.color : values.enabled.color}`,
  };
});
